import { toast } from 'ui/sonner'

import { apiClient } from '~/api/client'
import type { components } from '~/api/types'
import { config } from '~/config/env'
import i18n from '~/i18n'
import { useSettingsStore } from '~/stores/settings'
import { urlBase64ToUint8Array } from '~/utils/url-base64-to-uint8-array'

export type SetupNotificationsResult =
  | { endpoint: string; ok: true }
  | { ok: false; reason: 'unsupported' | 'denied' | 'not_granted' | 'subscription_error' | 'was-denied' }

const mapBrowserPushSubscriptionToDto = (
  subscription: PushSubscription,
): components['schemas']['PushSubscriptionDto'] => {
  const { keys } = subscription.toJSON()

  if (!keys?.p256dh || !keys?.auth) throw new Error('Push subscription is missing required encryption keys')

  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: keys.p256dh,
      auth: keys.auth,
    },
  }
}

/** Drops the browser push subscription for this origin (best-effort). */
export const removeBrowserPushSubscription = async (): Promise<void> => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return

    await subscription.unsubscribe()
  } catch (error) {
    console.warn({ message: 'Failed to remove browser push subscription', error })
  }
}

export const setupNotifications = async (): Promise<SetupNotificationsResult> => {
  if (typeof Notification === 'undefined') {
    console.warn({ message: 'Notification API is not available' })

    return { ok: false, reason: 'unsupported' }
  }

  if (Notification.permission === 'denied') return { ok: false, reason: 'was-denied' }

  const permission =
    Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission

  if (permission === 'default') return { ok: false, reason: 'not_granted' }

  if (permission === 'denied') return { ok: false, reason: 'denied' }

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.VITE_VAPID_PUBLIC_KEY),
    })

    const { sourceLanguage, learnLanguage } = useSettingsStore.getState()
    const source = sourceLanguage?.value
    const learn = learnLanguage?.value

    await apiClient.POST('/push/subscribe', {
      body: {
        ...mapBrowserPushSubscriptionToDto(subscription),
        ...(source && learn ? { source, learn } : {}),
      },
    })

    return { endpoint: subscription.endpoint, ok: true }
  } catch (error) {
    const { sourceLanguage, learnLanguage } = useSettingsStore.getState()
    toast.error(i18n.t('Network error'))
    console.error({
      message: 'Failed to set up push notifications',
      error,
      sourceLanguage: sourceLanguage?.value,
      learnLanguage: learnLanguage?.value,
    })

    return { ok: false, reason: 'subscription_error' }
  }
}
